"use client"

import { cn } from "@/lib/utils"

// The chair itself, drawn rather than photographed.
//
// The product references this follows (a car, a pair of headphones) lead with
// the object and put the numbers underneath — you know what you're looking at
// before you read a word. A booth's object is the chair, so it gets the same
// treatment: one silhouette, front-on, no perspective tricks.
//
// Monochrome on purpose. Colour-coding six chairs turns the plan into a
// traffic light an owner has to decode; ink alone ranks them just as well and
// the card stays quiet. A busier chair is simply a darker chair.
//
// The shading is CONTINUOUS, not bucketed. Buckets meant 78% and 56% rendered
// identically while 89% jumped to near-black, so the darkness read as arbitrary
// rather than as the number it encodes. Now it interpolates.
//
// Colour comes from `currentColor` plus per-shape opacity, not from fill-*
// utilities — a silently-unresolved fill renders an invisible chair, and that
// failure mode is worth designing out.

const BODY = { min: 0.14, max: 0.66 }
const ACCENT = { min: 0.24, max: 0.9 }

const lerp = (r: { min: number; max: number }, t: number) => r.min + (r.max - r.min) * t

export function BarberChair({
  pct,
  down = false,
  className,
}: {
  /** Occupancy 0–100, or null when there's nothing to report. */
  pct: number | null
  /** Out of service — drawn faintest, regardless of history. */
  down?: boolean
  className?: string
}) {
  const t = down ? 0 : Math.min(1, Math.max(0, (pct ?? 0) / 100))
  const idle = down || pct == null || pct <= 0

  const body = idle ? BODY.min : lerp(BODY, t)
  const accent = idle ? ACCENT.min : lerp(ACCENT, t)

  return (
    <svg
      viewBox="0 0 96 112"
      width="100%"
      height="100%"
      fill="currentColor"
      aria-hidden="true"
      className={cn(idle ? "text-muted-foreground" : "text-foreground", className)}
    >
      {/* Every form is a circle or a pill — no square corners anywhere, so the
          silhouette reads as round at 58px the way the reference objects do. */}

      {/* Headrest */}
      <circle cx="48" cy="14" r="9" opacity={accent} />

      {/* Backrest — a pill, not a rounded box. */}
      <rect x="29" y="26" width="38" height="34" rx="17" opacity={body} />

      {/* Armrests — the detail that makes it read as a barber chair rather
          than a dining chair at this size. */}
      <circle cx="19" cy="66" r="8" opacity={accent} />
      <circle cx="77" cy="66" r="8" opacity={accent} />

      {/* Seat */}
      <rect x="24" y="59" width="48" height="18" rx="9" opacity={body} />

      {/* Hydraulic column */}
      <rect x="43" y="76" width="10" height="16" rx="5" opacity={accent} />

      {/* Base */}
      <ellipse cx="48" cy="98" rx="23" ry="7" opacity={accent} />
    </svg>
  )
}
